// Coarse, non-identifying environment context attached to every batch. We keep
// this intentionally low-resolution (browser family, OS family, a viewport
// bucket) so it describes the audience without fingerprinting an individual. No
// IP, no precise UA string, no geolocation.
import { getRouteSurface } from '@/lib/hostRouting'

export const APP_VERSION = (import.meta.env?.VITE_APP_VERSION || '').toString().trim() || 'dev'

export function getSurface() {
  if (typeof window === 'undefined') return 'studio'
  const surface = getRouteSurface({ pathname: window.location.pathname })
  return surface === 'studio' ? 'studio' : 'demo'
}

export function getPage() {
  if (typeof window === 'undefined') return ''
  // Path only. Query strings can carry tokens/next-paths, so they are dropped.
  return window.location.pathname || '/'
}

function detectBrowser(ua) {
  if (/edg\//i.test(ua)) return 'edge'
  if (/opr\/|opera/i.test(ua)) return 'opera'
  if (/chrome|crios/i.test(ua)) return 'chrome'
  if (/firefox|fxios/i.test(ua)) return 'firefox'
  if (/safari/i.test(ua)) return 'safari'
  return 'other'
}

function detectOs(ua) {
  if (/windows/i.test(ua)) return 'windows'
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  if (/mac os x|macintosh/i.test(ua)) return 'macos'
  if (/android/i.test(ua)) return 'android'
  if (/linux/i.test(ua)) return 'linux'
  return 'other'
}

function viewportBucket(width) {
  if (!Number.isFinite(width) || width <= 0) return 'unknown'
  if (width < 640) return 'mobile'
  if (width < 1024) return 'tablet'
  if (width < 1440) return 'laptop'
  return 'desktop'
}

export function getUaContext() {
  if (typeof navigator === 'undefined') {
    return { browser: 'other', os: 'other', viewport: 'unknown' }
  }
  const ua = navigator.userAgent || ''
  const width = typeof window !== 'undefined' ? window.innerWidth : 0
  return {
    browser: detectBrowser(ua),
    os: detectOs(ua),
    viewport: viewportBucket(width),
  }
}

// Only the registrable domain of the referrer, never the full URL (which can
// carry query params / tokens from the referring site).
export function getReferrerDomain() {
  if (typeof document === 'undefined' || !document.referrer) return ''
  try {
    const url = new URL(document.referrer)
    if (url.hostname === window.location.hostname) return ''
    return url.hostname
  } catch {
    return ''
  }
}
